import { useEffect, useMemo, useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import type {
  DataGridProProps,
  GridColumnVisibilityModel,
  GridPinnedColumns,
} from "@mui/x-data-grid-pro";

type Options = {
  /**
   * Field name of the column that should remain visible on mobile (pinned left).
   * Typical: the identifier ("name", "customerName", "id"). Optional.
   */
  pinFirst?: string;
  /**
   * Fields to hide on mobile. Anything not in this list stays visible.
   */
  hideOnMobile?: string[];
  /**
   * Extra column visibility (merged on top of mobile defaults).
   */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /**
   * Set to true if the page renders a mobile-only "summary" column whose
   * field is `mobileSummary`. The hook will hide it on desktop and show it
   * on mobile (replacing the per-field columns it summarizes).
   */
  hasMobileSummaryColumn?: boolean;
};

/**
 * Returns a bundle of MUI X DataGrid props tuned for mobile/tablet:
 *  1. full width container
 *  2. pinned first column (Pro)
 *  3. compact density
 *  8. reduced cell padding
 *  9. larger tap target via auto row height + min row height
 *
 * Spread the result onto a `<DataGridPro {...mobileProps} />` and merge sx with
 * any page-level sx.
 *
 * The hook owns internal state for `columnVisibilityModel` and `pinnedColumns`
 * so user interactions via the column menu (hide/show) and the pinning UI
 * persist across renders. When the viewport crosses the `md` breakpoint
 * the user overrides are cleared and the responsive defaults re-apply.
 *
 * Memoization deps are primitives so inline option literals at the call
 * site (the common pattern) don't defeat the memo. An unstable `options`
 * reference would otherwise force a new `columnVisibilityModel` /
 * `pinnedColumns` object every parent render, effectively preventing the
 * grid from honoring user-initiated column toggling.
 *
 * Usage:
 *   const mobileProps = useResponsiveDataGridProps({
 *     pinFirst: "customerName",
 *     hideOnMobile: ["email", "phone", "agencyName"],
 *   });
 *   <DataGridPro {...mobileProps} {...rest} />
 */
export function useResponsiveDataGridProps(
  options: Options = {},
): Partial<DataGridProProps> {
  const {
    pinFirst,
    hideOnMobile = [],
    columnVisibilityModel: extraVisibility,
    hasMobileSummaryColumn,
  } = options;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Primitive-based dep keys so downstream memos don't rebuild whenever
  // the caller passes a fresh inline object / array (the common case).
  const hideOnMobileKey = hideOnMobile.join("\u0000");
  const extraVisibilityKey = JSON.stringify(extraVisibility ?? null);

  // Responsive default `columnVisibilityModel` (before user overrides).
  const defaultVisibility = useMemo<GridColumnVisibilityModel>(() => {
    const extra: GridColumnVisibilityModel = extraVisibility ?? {};
    const model: GridColumnVisibilityModel = { ...extra };
    if (isMobile) {
      hideOnMobile.forEach((field) => {
        model[field] = false;
      });
      if (hasMobileSummaryColumn) {
        model.mobileSummary = true;
      }
    } else if (hasMobileSummaryColumn) {
      model.mobileSummary = false;
    }
    return model;
    // `hideOnMobile` / `extraVisibility` are read via their stable string
    // keys; listing them directly would defeat the memo for inline callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, hideOnMobileKey, extraVisibilityKey, hasMobileSummaryColumn]);

  const defaultPinnedColumns = useMemo<GridPinnedColumns | undefined>(
    () => (isMobile && pinFirst ? { left: [pinFirst] } : undefined),
    [isMobile, pinFirst],
  );

  // User overrides from the column menu / pinning UI. Cleared when the
  // viewport category flips so the new responsive defaults take effect.
  const [userVisibility, setUserVisibility] = useState<
    GridColumnVisibilityModel | undefined
  >(undefined);
  const [userPinned, setUserPinned] = useState<GridPinnedColumns | undefined>(
    undefined,
  );

  useEffect(() => {
    setUserVisibility(undefined);
    setUserPinned(undefined);
  }, [isMobile]);

  // Stable sx objects so DataGridPro doesn't re-process style rules every
  // parent render.
  const desktopSx = useMemo(() => ({ width: "100%" }), []);
  const mobileSx = useMemo(
    () => ({
      // Edge-to-edge: span the full viewport width and use negative side
      // margins to escape any parent page padding so the grid aligns with
      // the screen edges on mobile. (Pattern: `calc(50% - 50vw)` pulls the
      // element to viewport edge regardless of its container's padding.)
      width: "100vw",
      marginLeft: "calc(50% - 50vw)",
      marginRight: "calc(50% - 50vw)",
      borderRadius: 0,
      borderLeft: "none",
      borderRight: "none",
      // Tight horizontal padding so narrow viewports show more content
      // without waste on either side of each cell.
      "& .MuiDataGrid-cell": {
        paddingLeft: "6px !important",
        paddingRight: "6px !important",
      },
      "& .MuiDataGrid-columnHeader": {
        paddingLeft: "6px !important",
        paddingRight: "6px !important",
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        padding: 0,
      },
    }),
    [],
  );

  return useMemo<Partial<DataGridProProps>>(() => {
    const columnVisibilityModel = userVisibility ?? defaultVisibility;
    const pinnedColumns = userPinned ?? defaultPinnedColumns;

    const props: Partial<DataGridProProps> = isMobile
      ? {
          density: "compact",
          // Auto-size rows so multi-line "mobile summary" cells (name +
          // status + contact + type + dates) are not clipped by the default
          // compact row height. Pages using this hook render their summary
          // with `whiteSpace: normal` / `wordBreak: break-word`, which this
          // pairs with to expand the row to fit its content.
          getRowHeight: () => "auto",
          sx: mobileSx,
        }
      : {
          density: "standard",
          sx: desktopSx,
        };

    // Only emit a visibility model when there is something to set; passing
    // an empty object can confuse the grid's internal column state.
    if (Object.keys(columnVisibilityModel).length > 0) {
      props.columnVisibilityModel = columnVisibilityModel;
      props.onColumnVisibilityModelChange = setUserVisibility;
    }
    if (pinnedColumns) {
      props.pinnedColumns = pinnedColumns;
      props.onPinnedColumnsChange = setUserPinned;
    }

    // NOTE: We deliberately do NOT apply CSS width overrides to the pinned
    // column here. CSS `width` only changes how the cell paints; it does
    // not change the column's logical layout width inside the grid, which
    // leaves a visible gap between the (visually narrowed) pinned cell
    // and the next column. Each page must set an appropriate mobile size
    // on the column definition itself (via `minWidth` / `width`).
    return props;
  }, [
    isMobile,
    defaultVisibility,
    defaultPinnedColumns,
    userVisibility,
    userPinned,
    desktopSx,
    mobileSx,
  ]);
}

export default useResponsiveDataGridProps;
